if (!window.OTFCartAdd) {
  // One add-to-cart path for the quick-buy dialog form and the card Quick add control: the same request
  // ownership (the CartItems mutation lock when it exists), the same error contract, the same cart refresh
  // and the same cart popup sequence. Callers only differ in where the feedback is shown.
  //
  // Purchase intent is owned per exact variant while its request is in flight. A second activation for the
  // same variant from ANY entry point (the card Quick add, the dialog Add, another card showing the same
  // product) sends no second request and takes no second mutation: it follows the request that already owns
  // the intent and settles with its truthful outcome. Different variants stay independent, and a completed or
  // failed intent is released at once so a later deliberate add of the same variant is a new request.
  const otfPendingIntents = new Map();
  const otfIntentKey = (body) => {
    const id = body && typeof body.get === 'function' ? body.get('id') : null;
    return id ? 'variant:' + String(id).trim() : null;
  };
  const otfRunAdd = (context) => {
      const {
        body,
        submitButton,
        cart = document.querySelector('mini-cart-popup') || document.querySelector('cart-popup'),
        source = 'product-form',
        handleErrorMessage = () => {},
        getError = () => false,
        setError = () => {},
        onRejected = () => {},
        closeModal = null,
        onAdded = () => {}
      } = context;
      const cartState = typeof CartItems !== 'undefined' && CartItems.beginMutation ? CartItems : null;
      if (cartState?.recoveryRelease) {
        handleErrorMessage(window.cartStrings.recoveryError);
        return Promise.resolve(false);
      }

      handleErrorMessage();

      submitButton?.setAttribute('aria-disabled', true);
      submitButton?.classList.add('btn--loading');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = body;
      if (cart) {
        formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
        cart.setActiveElement(document.activeElement);
      }
      config.body = formData;

      const overlayIntent = typeof quickAddOverlayIntent !== 'undefined' ? quickAddOverlayIntent.current : null;
      const ownsOverlay = () => typeof quickAddOverlayIntent === 'undefined' || quickAddOverlayIntent.current === overlayIntent;
      let cartAddSucceeded = false;
      const finishMutation = cartState?.beginMutation();
      const request = cartState
        ? cartState.fetchCartText(routes.cart_add_url, config, new AbortController(), true).then(text => JSON.parse(text))
        : fetch(routes.cart_add_url, config).then(response => response.json());
      const openCartPopup = () => {
        if (!ownsOverlay()) return;
        if($(document).find('[data-popup-type="cart-popup"]').length){
          $(document).find('quick-add-modal').addClass('hide-popup');
          $(document).find('[data-popup-type="cart-popup"]').removeClass('hide-popup');
          $(document).find('body').attr('active-popup','cart');
          $(document).find('.theme-popup-overlay').removeClass('hidden-overlay');
          setTimeout(function(){
            $(document).find('.cart--popup-recommandation').addClass('recommandation-active');
          },450);
        }
      };
      return request
        .then(async (response) => {

          if (response.status || response.errors) {
            publish(PUB_SUB_EVENTS.cartError, {source, productVariantId: formData.get('id'), errors: response.description, message: response.message});
            const errorMessage = response.description || response.message || (typeof response.errors === 'string' ? response.errors : window.cartStrings.error);
            if (cartState) {
              await finishMutation(true);
            }
            handleErrorMessage(errorMessage);
            onRejected(errorMessage, response);
            return false;
          } else if (!cart) {
            window.location = window.routes.cart_url;
            return true;
          }

          cartAddSucceeded = true;
          if (!getError()) publish(PUB_SUB_EVENTS.cartUpdate, {source, productVariantId: formData.get('id')});
          setError(false);
          if (closeModal) {
            return new Promise((resolve, reject) => {
              if (ownsOverlay()) closeModal();
              setTimeout(() => {
                try {
                  if (cartState && !cartState.canRenderSnapshot()) { onAdded(response); resolve(true); return; }
                  cart.renderContents(response);
                  cart.classList.remove('is-empty');
                  openCartPopup();
                  onAdded(response);
                  resolve(true);
                } catch (error) {
                  reject(error);
                }
              });
            });
          } else {
            if (cartState && !cartState.canRenderSnapshot()) { onAdded(response); return true; }
            cart.renderContents(response);
            cart.classList.remove('is-empty');
            openCartPopup();
            onAdded(response);
            return true;
          }
        })
        .catch(async (e) => {
          console.error(e);
          if (cartState) {
            await finishMutation(true);
            handleErrorMessage(window.cartStrings.error);
            onRejected(window.cartStrings.error, null);
          } else if (cartAddSucceeded) {
            window.location = window.routes.cart_url;
          } else {
            handleErrorMessage(window.cartStrings.error);
            onRejected(window.cartStrings.error, null);
          }
          return false;
        })
        .finally(() => {
          finishMutation?.();
          submitButton?.classList.remove('btn--loading');
          if (!getError()) submitButton?.removeAttribute('aria-disabled');
        });
  };
  window.OTFCartAdd = {
    // True while a request for this exact variant is in flight through this path.
    pending(variantId) {
      return otfPendingIntents.has('variant:' + String(variantId ?? '').trim());
    },
    submit(context) {
      const key = otfIntentKey(context.body);
      const owner = key ? otfPendingIntents.get(key) : null;
      if (owner) {
        if (typeof context.onDuplicate === 'function') context.onDuplicate(owner);
        return owner.then(result => result, () => false);
      }
      const request = otfRunAdd(context);
      if (key) {
        otfPendingIntents.set(key, request);
        const release = () => { if (otfPendingIntents.get(key) === request) otfPendingIntents.delete(key); };
        request.then(release, release);
      }
      return request;
    }
  };
}
if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('form');
      this.form.querySelector('[name=id]').disabled = !this.form.querySelector('[name=id]').value;
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.cart = document.querySelector('mini-cart-popup') || document.querySelector('cart-popup');
      this.submitButton = this.querySelector('[type="submit"]');
      if (document.querySelector('cart-popup')) this.submitButton.setAttribute('aria-haspopup', 'dialog');
    }

    onSubmitHandler(evt) {
      evt.preventDefault();
      const variantInput = this.form.querySelector('[name="id"]');
      if (this.submitButton.disabled || this.submitButton.getAttribute('aria-disabled') === 'true' || variantInput?.disabled || !variantInput?.value) return;
      const quickAddModal = this.closest('quick-add-modal');
      const overlayIntent = typeof quickAddOverlayIntent !== 'undefined' ? quickAddOverlayIntent.current : null;
      const ownsOverlay = () => typeof quickAddOverlayIntent === 'undefined' || quickAddOverlayIntent.current === overlayIntent;
      let duplicate = false;
      return window.OTFCartAdd.submit({
        body: new FormData(this.form),
        submitButton: this.submitButton,
        cart: this.cart,
        source: 'product-form',
        handleErrorMessage: (message) => this.handleErrorMessage(message),
        getError: () => this.error,
        setError: (value) => { this.error = value; },
        onDuplicate: () => {
          // This exact variant is already being added from another entry point: wait for that request.
          duplicate = true;
          this.handleErrorMessage();
          this.submitButton.setAttribute('aria-disabled', true);
          this.submitButton.classList.add('btn--loading');
        },
        closeModal: quickAddModal ? () => quickAddModal.hide(true) : null
      }).then((added) => {
        if (duplicate) {
          this.submitButton.classList.remove('btn--loading');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          if (added && quickAddModal && ownsOverlay()) quickAddModal.hide(true);
          else if (!added) this.handleErrorMessage(window.cartStrings.error);
        }
        return added;
      });
    }

    handleErrorMessage(errorMessage = false) {
      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      if(errorMessage['email'] == undefined){
        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
      }

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
  });
}
if (!window.__otfProductFormResponsiveInitialized) {
  window.__otfProductFormResponsiveInitialized = true;
if(window.matchMedia("(min-width: 990px)").matches && $(document).find('.visible-on__sticky').length){
  $(window).on('scroll',function(){
    if($(document).find('.variant-picker__end').length){
      var scrollTop = $(window).scrollTop();
      var divOffset = $(document).find('.variant-picker__end').offset().top;
      var checkStickyBottom = isStickyElementAtBottom();
      if (scrollTop >= divOffset) {
        if(checkStickyBottom == 'bottom-false'){
          $(document).find('.visible-on__sticky').stop(true, true).slideDown('slow');
        }
      } else {
        $(document).find('.visible-on__sticky').stop(true, true).slideUp('slow');
      }
    }
  });
  function isStickyElementAtBottom() {
    const $mainDiv = $('.main-product__layout');
    const $childDiv = $('.product-info__right-sticky');
    const childOffsetTop = $childDiv.offset().top;
    const mainScrollTop = $mainDiv.scrollTop();
    const mainOffsetTop = $mainDiv.offset().top;
    const childHeight = $childDiv.height();
    const mainHeight = $mainDiv.height();
    const isAtBottom = (childOffsetTop + childHeight + 45) >= (mainScrollTop + mainOffsetTop + mainHeight);
    if (isAtBottom) {
      return 'bottom-true';
    } else {
      return 'bottom-false';
    }
  }
}
if(window.matchMedia("(max-width: 989px)").matches){
  if($(document).find('.product-info__right-sticky').length){
    var destinationMobile = document.querySelector(".product__info-wrapper");
    var leftInfoDesktop = document.querySelector(".product-media__left-info");
    var stickyDiv = document.querySelector(".product-info__right-sticky");
    if (destinationMobile) destinationMobile.appendChild(stickyDiv);
    stickyDiv.classList.add('mobile-appended');
  }
}
window.addEventListener("resize", function() {
  if($(document).find('.product-info__right-sticky').length){
    var destinationMobile = document.querySelector(".product__info-wrapper");
    var leftInfoDesktop = document.querySelector(".product-media__left-info");
    var stickyDiv = document.querySelector(".product-info__right-sticky");
    if(window.matchMedia("(max-width: 989px)").matches){
      if (!stickyDiv.classList.contains('mobile-appended')) {
        if (destinationMobile) destinationMobile.appendChild(stickyDiv);
        stickyDiv.classList.remove('desktop-appended');
        stickyDiv.classList.add('mobile-appended');
      }
    }else{
      if (!stickyDiv.classList.contains('desktop-appended')) {
        if (leftInfoDesktop?.parentNode) leftInfoDesktop.parentNode.insertBefore(stickyDiv, leftInfoDesktop.nextSibling);
        stickyDiv.classList.remove('mobile-appended');
        stickyDiv.classList.add('desktop-appended');
      }
    }
  } 
});



}



